"use client";

import { usePathname, useRouter } from "@/i18n/navigation";
import { useSession } from "@/lib/auth/client";
import { ClientToServerEvents, ServerToClientEvents } from "@satranc365/shared";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { io, Socket } from "socket.io-client";

type ChessSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
interface ChessSocketContextValue {
    socket: ChessSocket | null;
    isConnected: boolean;
    isPending: boolean;
    error: string | null;
}

const ChessSocketContext = createContext<ChessSocketContextValue | null>(null);

interface ChessSocketProviderProps {
    children: ReactNode;
}

export function ChessSocketProvider({ children }: ChessSocketProviderProps) {
    const { data: session, isPending: isSessionPending } = useSession();
    const router = useRouter();
    const pathname = usePathname();

    const socketRef = useRef<ChessSocket | null>(null);
    const [socketInstance, setSocketInstance] = useState<ChessSocket | null>(
        null,
    );

    const [isPending, setIsPending] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const authenticatedUserId = session?.user?.id;

    // Read inside the connection effect without being its dependencies: the
    // socket must survive navigation. Recreating it on every pathname change
    // dropped the player's connection (and tournament room) on each page
    // switch — pvp-chess-server then saw them as offline and skipped them
    // in Arena pairing.
    const pathnameRef = useRef(pathname);
    const routerRef = useRef(router);
    useEffect(() => {
        pathnameRef.current = pathname;
        routerRef.current = router;
    }, [pathname, router]);

    useEffect(() => {
        if (authenticatedUserId || isSessionPending) return;
        if (pathname.startsWith("/oyun/")) {
            router.push(`/login?from=${encodeURIComponent(pathname)}`);
        }
    }, [authenticatedUserId, isSessionPending, pathname, router]);

    useEffect(() => {
        if (!authenticatedUserId) return;

        const serverUrl = process.env.NEXT_PUBLIC_PVP_WS_URL;
        if (!serverUrl) {
            queueMicrotask(() => setError("Websocket url is not provided"));
            return;
        }

        queueMicrotask(() => setIsPending(true));
        let socket: ChessSocket | null = null;
        let cancelled = false;

        const onConnect = () => {
            setIsPending(false);
            setIsConnected(true);
            setError(null);
        };

        const onDisconnect = () => {
            setIsPending(false);
            setIsConnected(false);
        };

        const onError = (err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            console.error("PvP socket connect_error:", message);
            setIsPending(false);
            setError(message);
            const currentPath = pathnameRef.current;
            if (
                err instanceof Error &&
                err.message === "AUTH_ERROR" &&
                currentPath.startsWith("/oyun/")
            ) {
                routerRef.current.push(
                    `/login?from=${encodeURIComponent(currentPath)}`,
                );
            }
        };

        const connectSocket = async () => {
            try {
                setIsPending(true);

                const response = await fetch("/api/pvp/token", {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error("PVP_AUTH_TOKEN_ERROR");
                }

                const data = (await response.json()) as { token?: string };

                if (!data.token) {
                    throw new Error("PVP_AUTH_TOKEN_ERROR");
                }

                if (cancelled) return;

                socket = io(serverUrl, {
                    auth: { sessionToken: data.token },
                    autoConnect: false,
                    transports: ["websocket"],
                });

                socketRef.current = socket;
                setSocketInstance(socket);

                socket.on("connect", onConnect);
                socket.on("disconnect", onDisconnect);
                socket.on("connect_error", onError);

                socket.connect();
            } catch (err) {
                if (cancelled) return;
                const message =
                    err instanceof Error ? err.message : String(err);
                setIsPending(false);
                setError(message);
            }
        };

        void connectSocket();

        return () => {
            cancelled = true;

            socket?.disconnect();

            socket?.off("connect", onConnect);
            socket?.off("disconnect", onDisconnect);
            socket?.off("connect_error", onError);

            socketRef.current = null;
            setSocketInstance(null);
        };
    }, [authenticatedUserId]);

    // Tournament pairings can start while the player is browsing anywhere
    // else on the site (e.g. still on the tournament page) — this socket is
    // global (mounted at the root layout), so it's the one place that can
    // catch `game_started` regardless of what page is currently open and get
    // them into their game before the first-move grace period runs out.
    // No-ops when already on that exact room (normal in-room game starts —
    // ready-up, rematch — are handled locally by PvpChessGameProvider).
    useEffect(() => {
        if (!socketInstance) return;
        // `roomCode` isn't in the installed @satranc365/shared's `game_started`
        // type yet — see berserk-socket-types.ts for the same predates-the-
        // package situation.
        const socket = socketInstance as unknown as Socket<{
            game_started: (data: { roomCode?: string }) => void;
        }>;
        const onGameStarted = (data: { roomCode?: string }) => {
            const roomCode = data.roomCode;
            if (!roomCode) return;
            if (pathname === `/oyun/${roomCode}`) return;
            router.push(`/oyun/${roomCode}`);
        };
        socket.on("game_started", onGameStarted);
        return () => {
            socket.off("game_started", onGameStarted);
        };
    }, [socketInstance, pathname, router]);

    const value = useMemo<ChessSocketContextValue>(
        () => ({
            socket: socketInstance,
            isConnected,
            isPending,
            error,
        }),
        [socketInstance, isConnected, isPending, error],
    );

    return (
        <ChessSocketContext.Provider value={value}>
            {children}
        </ChessSocketContext.Provider>
    );
}

export function useChessSocketContext(): ChessSocketContextValue {
    const context = useContext(ChessSocketContext);
    if (!context) {
        throw new Error(
            "useChessSocketContext must be used within a ChessSocketProvider",
        );
    }
    return context;
}
